import React, { useState, useMemo } from 'react';
import DateRangePopover from './DateRange';
import SelectCheckbox from '../../../../shared/select-checkbox';
import Button from '../../../../shared/button';
import { PartnerGuard } from '../../../../guards/RolesGuard';
import { FEEDBACK_TAXONOMY } from '../../../../utils/constants/feedback-taxonomy';
import { useTheme } from '../../../../utils/theme-context';

interface ITripFilters {
    allTrips?: any;
    staff?: any;
    selectedStaffRole: any;
    selectedStaff: any;
    selectedCabin?: string;
    selectedTrip: any;
    selectedDateRange: any;
    trainManagers: any;
    selectedTrainManager: any;
    onClickTrip: (type: string) => void;
    onChangeFilters: (value: any, type?: string) => void;
    onChangeCabin?: (value: string) => void;
    onChangeDateRange: (range: any) => void;
    onClickClearAll: () => void;
    title?: string;
    hideActions?: boolean;
}

/* ── Taxonomy Modal ─────────────────────────────────────────────── */
const TaxonomyModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    const categories = FEEDBACK_TAXONOMY.categories;
    const subcategories = FEEDBACK_TAXONOMY.subcategories as Record<string, string[]>;
    if (!open) return null;

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40' onClick={onClose}>
            <div className='bg-white dark:bg-[#111317] dark:text-white rounded-xl shadow-xl w-full max-w-[640px] max-h-[85vh] flex flex-col' onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className='flex items-center justify-between px-6 py-4 border-b'>
                    <p className='text-[18px] font-medium font-inter text-[#000000] dark:text-white'>Taxonomy</p>
                    <button onClick={onClose} className='text-gray-400 hover:text-gray-600 transition'>
                        <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='black'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className='px-6 py-4 overflow-y-auto flex-1'>
                    <p className='text-[12px] text-[#717680] dark:text-white font-normal font-inter mb-5'>Each section has the categories and subcategories listed below.</p>
                    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6'>
                        {categories.map((cat) => {
                            const subs = subcategories[cat] || [];
                            return (
                                <div key={cat} className='flex flex-col'>
                                    <p className='text-sm font-normal text-black dark:text-white font-inter mb-2'>{cat}</p>
                                    <div className='border border-[#AFAFAF] rounded-lg p-3 flex-1'>
                                        {subs.length > 0 ? (
                                            <ul className='space-y-1'>
                                                {subs.map((sub) => (
                                                    <li key={sub} className='text-sm text-[#0A0D12] dark:text-white font-normal font-inter'>{sub}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className='text-sm text-gray-400 italic'>No Subcategories</p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Footer */}
                <div className='px-6 py-4'>
                    <button
                        onClick={onClose}
                        className='w-full py-2.5 rounded-lg bg-[#252B37] text-white text-sm font-medium hover:bg-gray-800 transition'
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ── Format Date for Filter Pill ──────────────────────────────── */
const formatDateShort = (d: Date) =>
    `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;

/* ── Trip Filters ──────────────────────────────────────────────── */
const TripFilters = ({
    allTrips,
    staff,
    selectedStaffRole,
    selectedTrip,
    selectedStaff,
    selectedCabin,
    trainManagers,
    selectedTrainManager,
    selectedDateRange,
    onClickTrip,
    onChangeFilters,
    onChangeCabin,
    onChangeDateRange,
    onClickClearAll,
    title = 'Apply Filters',
    hideActions = false,
}: ITripFilters) => {
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [taxonomyOpen, setTaxonomyOpen] = useState(false);
	const { theme } = useTheme();
    const cabinOptions = useMemo(() => {
        const options: { label: string; value: string }[] = [];
        for (let charCode = 65; charCode <= 76; charCode += 1) {
            const carriage = String.fromCharCode(charCode);
            for (let cabinNum = 1; cabinNum <= 6; cabinNum += 1) {
                const cabinId = `${carriage}${cabinNum}`;
                options.push({ label: cabinId, value: cabinId });
            }
        }
        return options;
    }, []);
    const selectedCabinOption = useMemo(
        () => (selectedCabin?.trim() ? { label: selectedCabin.trim().toUpperCase(), value: selectedCabin.trim().toUpperCase() } : undefined),
        [selectedCabin],
    );

    // Active filters for pills
    const activeFilters = useMemo(() => {
        const pills: { label: string; key: string }[] = [];
        if (selectedTrip && selectedTrip?.value !== 'all') {
            pills.push({ label: `Journey: ${selectedTrip?.label || selectedTrip?.value}`, key: 'journey' });
        }
        if (selectedDateRange && selectedDateRange[0] && selectedDateRange[1]) {
            pills.push({
                label: `Date: ${formatDateShort(selectedDateRange[0])} - ${formatDateShort(selectedDateRange[1])}`,
                key: 'date',
            });
        }
        if (selectedTrainManager && selectedTrainManager?.value !== 'all') {
            pills.push({ label: `Train Manager: ${selectedTrainManager?.label || selectedTrainManager?.value}`, key: 'trainManager' });
        }
        if (selectedStaff && selectedStaff?.value !== 'all') {
            pills.push({ label: `Staff: ${selectedStaff?.label || selectedStaff?.value}`, key: 'staff' });
        }
        if (selectedStaffRole && selectedStaffRole?.value !== 'all') {
            pills.push({ label: `Role: ${selectedStaffRole?.label || selectedStaffRole?.value}`, key: 'staffRole' });
        }
        if (selectedCabin?.trim()) {
            pills.push({ label: `Cabin: ${selectedCabin.trim().toUpperCase()}`, key: 'cabin' });
        }
        return pills;
    }, [selectedTrip, selectedDateRange, selectedTrainManager, selectedStaff, selectedStaffRole, selectedCabin]);

    const handleRemoveFilter = (key: string) => {
        if (key === 'journey') onChangeFilters(undefined, 'journey');
        if (key === 'date') onChangeDateRange(undefined);
        if (key === 'trainManager') onChangeFilters(undefined, 'trainManager');
        if (key === 'staff') onChangeFilters(undefined, 'staff');
        if (key === 'staffRole') onChangeFilters(undefined, 'staffRole');
        if (key === 'cabin') onChangeCabin?.('');
    };

    return (
        <>
            <div className='my-2 rounded-xl border border-gray-200 bg-white dark:bg-[#111317] dark:border-white/10 shadow-sm'>
                {/* ── Header ─────────────────────────────────────────── */}
                <div
                    className='flex items-center justify-between px-6 py-3.5 rounded-tl-xl rounded-tr-xl'
                    style={{ backgroundColor: theme === 'dark' ? '#111317' : '#FAFAFA', borderBottom: theme === 'dark' ? '1px solid #ffffff1a' : '1px solid #F5F5F5' }}
                >
                    {/* Left: Filter icon + Apply Filters (clickable) */}
                    <button
                        onClick={() => setFiltersOpen((v) => !v)}
                        className='flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white hover:text-gray-700 transition'
                    >
                        <img src="/svg/filter_icon.svg" className="w-[15px] h-[15px] flex-shrink-0 dark:contrast-0" alt="filter" />
                        {/* <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z' />
                        </svg> */}
                        {title}
                    </button>

                    {/* Right: Buttons + Collapse toggle */}
                    <div className='flex items-center gap-4'>
                        {!hideActions && !PartnerGuard() && (
                            <Button
                                label='New Trip'
                                className='btn-primary-rounded-less dark:!bg-[#00691F] min-w-[100px] text-sm h-[32px] !rounded-[8px] !w-[100px] !text-[12px] font-bold font-plus-jakarta !shadow-none'
                                onClickButton={() => onClickTrip('new')}
                            />
                        )}
                        {!hideActions && (
                            <Button
                                label='Taxonomy'
                                className=' text-gray-800  !border-gray-300 hover:!bg-gray-50 h-[32px] min-w-[100px] !rounded-[8px] !w-[100px] text-[12px] font-bold font-plus-jakarta bg-[#E9EAEB] dark:text-white dark:bg-[#252B37] !shadow-none'
                                onClickButton={() => setTaxonomyOpen(true)}
                            />
                        )}

                        {/* Collapse arrows */}
                        <button onClick={() => setFiltersOpen((v) => !v)} className='ml-1 text-gray-500 hover:text-gray-700 transition'>
                            {filtersOpen ? (
                                <svg className='h-6 w-6 dark:contrast-0' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                                    {/* Open: top arrow points down, bottom arrow points up — pointing at each other */}
                                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 4l5 4 5-4' />
                                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 20l5-4 5 4' />
                                </svg>
                            ) : (
                                <svg className='h-6 w-6 dark:contrast-0' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                                    {/* Closed: top arrow points up, bottom arrow points down — pointing away */}
                                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 10l5-4 5 4' />
                                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 14l5 4 5-4' />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>

                {/* ── Collapsible Body ───────────────────────────────── */}
                <div
                    className='transition-all duration-500 ease-in-out'
                    style={{
                        maxHeight: filtersOpen ? '600px' : '0px',
                        opacity: filtersOpen ? 1 : 0,
                        overflow: filtersOpen ? 'visible' : 'hidden',
                    }}
                >
                    <div className='px-6 py-5 space-y-5'>
                        {/* Row 1: Select Journey + Itinerary Date */}
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                            <SelectCheckbox
                                name='Select Journey'
                                selectedOption={selectedTrip}
                                uniqueName='journey'
                                onChangeSelectedOption={onChangeFilters}
                                options={allTrips || []}
                                placeholder='Select...'
                                containerStyle='mb-0 w-full'
                            />
                            <DateRangePopover
                                label='Itinerary Date'
                                containerStyle=''
                                labelStyle='text-gray-500 dark:!text-gray-300 !font-inter !text-[#0A0D12] !text-[14px]'
                                value={selectedDateRange}
                                onChange={onChangeDateRange}
                            />
                        </div>

                        {/* Row 2: Train Manager + Head of Departments */}
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                            <SelectCheckbox
                                name='Train Manager'
                                options={trainManagers || []}
                                selectedOption={selectedTrainManager}
                                uniqueName='trainManager'
                                onChangeSelectedOption={onChangeFilters}
                                placeholder='Search by name'
                                containerStyle='mb-0 w-full'
                            />
                            <SelectCheckbox
                                name='Head of Departments'
                                options={staff || []}
                                selectedOption={selectedStaff}
                                uniqueName='staff'
                                onChangeSelectedOption={onChangeFilters}
                                placeholder='Search by name'
                                containerStyle='mb-0 w-full'
                            />
                        </div>

                        {/* Row 3: Cabin filter */}
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                            <SelectCheckbox
                                name='Cabin'
                                options={cabinOptions}
                                selectedOption={selectedCabinOption}
                                uniqueName='cabin'
                                onChangeSelectedOption={(value: any) => onChangeCabin?.(value?.value || '')}
                                placeholder='Type or select cabin (e.g. A1)'
                                containerStyle='mb-0 w-full'
                            />
                        </div>

                        {/* Row 2: Active filter pills + actions */}
                        <div className='flex flex-wrap items-center justify-between gap-3'>
                            <div className='flex flex-wrap items-center gap-2'>
                                {activeFilters.map((f) => (
                                    <span
                                        key={f.key}
                                        className='inline-flex items-center gap-1.5 rounded-md dark:bg-[#323849] bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 dark:text-[#DADDE6] border border-blue-200'
                                    >
                                        {f.label}
                                        <button
                                            onClick={() => handleRemoveFilter(f.key)}
                                            className='text-blue-500 hover:text-blue-700 dark:text-white'
                                        >
                                            <svg className='h-3.5 w-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                                            </svg>
                                        </button>
                                    </span>
                                ))}
                            </div>

                            <div className='flex items-center gap-4'>
                                <button
                                    onClick={onClickClearAll}
                                    className='flex items-center gap-2 rounded-[10px] h-[35px] bg-[#F1F5F9] border-gray-300 px-5 py-2.5 text-sm text-[#1E293B] font-bold !text-[12px] w-[115px] font-plus-jakarta hover:bg-gray-100 transition'
                                >
                                    {/* <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' />
                                    </svg> */}
                                    <img src='/svg/clear.svg' className='w-[12px] h-[12px] flex-shrink-0' alt='trip' />
                                    Clear All
                                </button>
                                <button
                                    onClick={() => {/* filters are applied live via onChange already */ }}
                                    className='  bg-[#1E293B] dark:bg-[#00691F] h-[35px] rounded-[10px] text-white text-sm font-medium hover:bg-gray-800 transition !text-[12px] w-[137px] font-plus-jakarta'
                                >
                                    Show
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Taxonomy Modal */}
            <TaxonomyModal open={taxonomyOpen} onClose={() => setTaxonomyOpen(false)} />
        </>
    );
};

export default TripFilters;
